import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import { isValidPassword } from "../utils/validation";
import { MESSAGES } from "../utils/messages";

const SetPassword: React.FC = () => {
  const { user, updatePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) {
    navigate("/login");
    return null;
  }

  const passwordChecks = [
    { label: "At least 8 characters", valid: newPassword.length >= 8 },
    { label: "One uppercase letter", valid: /[A-Z]/.test(newPassword) },
    { label: "One lowercase letter", valid: /[a-z]/.test(newPassword) },
    { label: "One number", valid: /[0-9]/.test(newPassword) },
    { label: "One special character", valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(MESSAGES.error.passwordMismatch);
      return;
    }

    const passwordError = isValidPassword(newPassword, {});
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    setLoading(true);
    try {
      const ok = await updatePassword(newPassword);
      if (!ok) {
        toast.error(MESSAGES.error.passwordResetFailed);
        return;
      }
      toast.success("Password updated. Please log in with your new password.");
      logout();
      navigate("/login");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || MESSAGES.error.passwordResetFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow w-96">
        <h2 className="text-xl font-semibold mb-4">Set a new password</h2>

        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="input-field w-full mb-3"
          required
        />

        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="input-field w-full mb-3"
          required
        />

        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <p className="text-sm text-red-600 mb-3">{MESSAGES.error.passwordMismatch}</p>
        )}

        <div className="bg-gray-50 rounded p-3 mb-4 space-y-1.5">
          {passwordChecks.map((check) => (
            <div key={check.label} className="flex items-center text-sm">
              <span className={`mr-2 ${check.valid ? "text-green-500" : "text-gray-400"}`}>
                {check.valid ? "✓" : "○"}
              </span>
              <span className={check.valid ? "text-green-600" : "text-gray-500"}>{check.label}</span>
            </div>
          ))}
        </div>

        <button className="btn-primary w-full" disabled={loading}>
          {loading ? "Setting..." : "Set Password"}
        </button>
      </form>
    </div>
  );
};

export default SetPassword;